import BqnContext from "common/BqnContext";
import { useContext, useEffect, useRef, useState } from "react";
import { getError } from "../utils";

const fromBinaryToBase64 = function (buf) {
  var bytes = new Uint8Array(buf),
    len = bytes.byteLength,
    charArray = new Array(len);
  for (var i = 0; i < len; i++) {
    charArray[i] = String.fromCharCode(bytes[i]);
  }
  return window.btoa(charArray.join(""));
};

const wontCancel = { current: false };
const SOFTWARE_INSTALL_TMOUT_MS = 300000;
const CHUNKMAXSIZE = 32 * 1024;
const doesNothing = () => {};
const INSTALL_BATCH_SIZE = 2;

export const useInstall = ({ whenDone = doesNothing }) => {
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [clusterInstallationStatus, setClusterInstallationStatus] =
    useState(null);
  const cancelledRef = useRef(false);
  const validResponse = useRef();
  const count = useRef(0);
  const errorFound = useRef();
  const packageMatch = useRef();
  const name = useRef();
  const version = useRef();
  const {
    bqns,
    onlineBqns,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const cancel = () => (cancelledRef.current = true);
  const unCancel = () => (cancelledRef.current = false);
  const clear = () => {
    unCancel();
    setError(null);
    setClusterInstallationStatus(null);
    setProgress(null);
  };
  const reportError = (error) => {
    setProgress(null);
    setError(error);
  };
  useEffect(() => clear(), []);
  const uploadProgress = (progress, total) =>
    setProgress({ upload: { progress, total } });
  const start = (file, bqnSelected, loginBqnId) => {
    return Promise.resolve(clear())
      .then(() => {
        if (mode === "cluster" && bqnSelected === "all") {
          setClusterInstallationStatus(
            bqns.reduce((acc, bqn) => {
              acc[bqn.bqnId] = {
                bqnId: bqn.bqnId,
                status: bqn.function === "offline" ? "offline" : "waiting",
              };
              return acc;
            }, {})
          );
        }
        const remotePath = "/tmp/sw_" + Math.random().toString(36).substring(5);
        return uploadInto(remotePath, file, {
          cancelledRef,
          progress: uploadProgress,
        });
      })
      .then((remotePath) => {
        if (bqnSelected === undefined && loginBqnId === undefined) {
          return remotePath;
        } else if (
          bqnSelected !== "all" &&
          parseFloat(bqnSelected) !== parseFloat(loginBqnId)
        ) {
          const cmd = `file scp bqn-id ${loginBqnId} ${remotePath} bqn-id ${bqnSelected} ${remotePath}`;
          return ifCl.run(cmd).then((res) => {
            if (res) {
              throw new Error(`Error installing software: ${res}`);
            } else {
              return remotePath;
            }
          });
        } else {
          return remotePath;
        }
      })
      .then((remotePath) => {
        setProgress({ upload: true, installing: true, bqnSelected });

        if (mode === "cluster" && bqnSelected === "all") {
          setClusterInstallationStatus(
            bqns.reduce((acc, bqn) => {
              acc[bqn.bqnId] = { bqnId: bqn.bqnId, status: "installing" };
              return acc;
            }, {})
          );
          return installBpkgAll(remotePath, file);
        } else {
          return installBpkg(remotePath, bqnSelected);
        }
      })
      .then((response) => {
        setProgress((previous) => ({
          ...previous,
          completed: true,
          name: file.name,
          nameBpkg: response.Name,
          versionBpkg: response.Version,
        }));
      })
      .then(whenDone)
      .catch(reportError);
  };

  const installBpkgAll = (remotePath, file) => {
    count.current = 0;
    errorFound.current = undefined;
    packageMatch.current = file.name.match(/(bqn|gui)-(.*).bpkg/);
    name.current = packageMatch.current ? packageMatch.current[1] : null;
    version.current = packageMatch.current ? packageMatch.current[2] : null;

    validResponse.current = {
      Name: name.current,
      Version: version.current,
    };

    return new Promise((resolve, reject) => {
      installBpkgBatch(remotePath, INSTALL_BATCH_SIZE, 0, resolve, reject)
    });
  };

  const installBpkgBatch = (remotePath, batchSize, offset, resolve, reject) => {
    const batch = onlineBqns.slice(offset, offset + batchSize);
    if (batch.length === 0) {
      return;
    }
    Promise.all(
      batch.map((bqn) => installAndReport(remotePath, bqn, resolve, reject))
      ).then(() => {
        installBpkgBatch(remotePath, batchSize, offset + batchSize, resolve, reject);
      });
  }

  const installAndReport = (remotePath, bqn, resolve, reject) => {
    return installBpkg(remotePath, bqn.bqnId)
      .then((response) => {
        const entry = {
          [bqn.bqnId]: {
            bqnId: bqn.bqnId,
            status: "done",
            ...response,
          },
        };

        setClusterInstallationStatus((prev) => ({
          ...(prev ?? {}),
          ...entry,
        }));

        validResponse.current = response;

        count.current++;
        if (count.current === onlineBqns.length) {
          if (errorFound.current) {
            reject("Error installing software for some BQN");
          } else {
            resolve(validResponse.current);
          }
        }
      })
      .catch((error) => {
        let entry = {};

        const alreadyInstalled =
          error && error.search("already installed") !== -1;

        if (alreadyInstalled) {
          entry[bqn.bqnId] = {
            bqnId: bqn.bqnId,
            status: "alreadyInstalled",
            Name: name.current,
            Version: version.current,
          };
        } else {
          entry[bqn.bqnId] = {
            bqnId: bqn.bqnId,
            status: "error",
            error: getError(error),
          };
        }

        setClusterInstallationStatus((prev) => ({
          ...(prev ?? {}),
          ...entry,
        }));

        if (error.search("already installed") === -1) {
          errorFound.current = error;
        }

        count.current++;
        if (count.current === onlineBqns.length) {
          if (errorFound.current) {
            reject("Error installing software for some BQN");
          } else {
            resolve(validResponse.current);
          }
        }
      });
  };

  return [clear, start, cancel, { progress, error, clusterInstallationStatus }];
};

const installBpkg = (remotePath, bqnSelected) =>
  new Promise((resolve, reject) => {
    const cmd = `system software install file://${remotePath}${
      bqnSelected ? ` bqn-id ${bqnSelected}` : ""
    }`;
    console.log("software.install.start");
    ifCl.executeWithOpts(
      cmd,
      SOFTWARE_INSTALL_TMOUT_MS,
      () => resolve({ error: "timeout during install" }),
      (res) => {
        const { Name, Version } = getNameAndVersion(res.CmdResponse.response);
        if (
          res.CmdResponse.code !== 0 ||
          res.CmdResponse.response.indexOf("%ERR") > -1
        ) {
          console.log("software.install.error");
          reject(`Error installing software: ${res.CmdResponse.response}`);
        } else {
          console.log("software.install.done");
          resolve({ Name, Version });
        }
      }
    );
  });

const getNameAndVersion = (input) => {
  return input
    .trim("\n")
    .split("\n")
    .reduce((acc, row) => {
      if (row.startsWith("Name:") || row.startsWith("Version:")) {
        const [key, value] = row.split(":");
        if (value !== undefined) {
          acc[key.trim()] = value.trim();
        }
      }
      return acc;
    }, {});
};

const uploadInto = (
  remotePath,
  source,
  { cancelledRef = wontCancel, progress = doesNothing }
) => {
  let offset = 0;
  progress(offset, source.size);
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    const next = () => {
      const blob = source.slice(offset, offset + CHUNKMAXSIZE);
      reader.readAsArrayBuffer(blob);
    };
    reader.onerror = (event) => reject(event.target.error);
    reader.onload = (event) => {
      if (cancelledRef.current === true) {
        reject("Cancelled");
        return;
      }
      if (offset >= source.size) {
        resolve(remotePath);
        return;
      }
      // File Chunk available
      const chunkInB64 = fromBinaryToBase64(event.target.result);
      ifCl.saveFile(
        chunkInB64.length,
        chunkInB64,
        // remotePath MUST be null after first chunk
        offset === 0 ? remotePath : null,
        (error) => reject(error),
        (res) => {
          if (res.SaveFileResponse.code === 0) {
            // Read next chunk
            offset += event.target.result.byteLength;
            progress(offset, source.size);
            next();
          } else {
            reject(`Failed while sending ${source.name}`);
          }
        }
      );
    };
    next();
  });
};
